/*
  MOANA – Lazy Video (Pagespeed)
  Videos mit [data-moana-lazy-video] laden erst, wenn
  1. die Seite fertig geladen ist (window "load" bzw. spätestens nach 4 s) und
  2. das Video sichtbar ist (IntersectionObserver).
  Vorher ist nur das Posterbild zu sehen. Verlässt das Video den sichtbaren
  Bereich, wird es pausiert. Unsichtbare Duplikate (z. B. Desktop-Video auf
  dem Handy) werden nie geladen.

  Attribute:
    data-mp4          Original-MP4 (Fallback, höchste Qualität)
    data-hls          HLS-Master-Playlist (wird nur gelesen, um kleinere MP4-Varianten zu finden)
    data-max-height   z. B. 720 → größte verfügbare MP4-Variante mit höchstens 720p
*/
(() => {
  if (window.__moanaLazyVideo) return;
  window.__moanaLazyVideo = true;

  const SELECTOR = "video[data-moana-lazy-video]";
  const saveData = !!(navigator.connection && navigator.connection.saveData);
  const playlistCache = new Map();

  // "abc.HD-720p.3.0Mbps.hls.67145858_.m3u8" -> "abc.HD-720p-3.0Mbps-67145858.mp4"
  const VARIANT_RE = /^(.+)\.((?:HD|SD)-(\d+)p)\.([\d.]+Mbps)\.hls\.(\d+)_\.m3u8$/;

  function getPlaylist(url) {
    if (!playlistCache.has(url)) {
      playlistCache.set(
        url,
        fetch(url, { credentials: "omit" })
          .then((r) => (r.ok ? r.text() : ""))
          .catch(() => ""),
      );
    }
    return playlistCache.get(url);
  }

  async function resolveSource(video) {
    const mp4 = video.dataset.mp4 || "";
    const hls = video.dataset.hls || "";
    const maxHeight = parseInt(video.dataset.maxHeight || "0", 10);
    if (!maxHeight || !hls) return mp4 || hls;

    try {
      const text = await getPlaylist(hls);
      const base = hls.split("?")[0];
      const dir = base.slice(0, base.lastIndexOf("/") + 1);
      const query = hls.includes("?") ? "?" + hls.split("?")[1] : "";
      let best = null;
      text.split("\n").forEach((line) => {
        const m = line.trim().match(VARIANT_RE);
        if (!m) return;
        const height = parseInt(m[3], 10);
        if (height <= maxHeight && (!best || height > best.height)) {
          best = { height, file: `${m[1]}.${m[2]}-${m[4]}-${m[5]}.mp4` };
        }
      });
      if (best) return dir + best.file + query;
    } catch (e) {
      /* Fallback unten */
    }
    return mp4 || hls;
  }

  async function load(video) {
    if (video.dataset.moanaState) return video._moanaReady;
    video.dataset.moanaState = "loading";
    video._moanaReady = (async () => {
      const src = await resolveSource(video);
      const fallback = video.dataset.mp4;
      if (fallback && src !== fallback) {
        video.addEventListener(
          "error",
          () => {
            if (video.getAttribute("src") !== fallback) {
              video.src = fallback;
              video.load();
              if (video._moanaWantsPlay) safePlay(video);
            }
          },
          { once: true },
        );
      }
      video.muted = true;
      video.preload = "auto";
      video.src = src;
      video.dataset.moanaState = "loaded";
    })();
    return video._moanaReady;
  }

  function safePlay(video) {
    const p = video.play();
    if (p && p.catch) p.catch(() => {});
  }

  async function show(video) {
    video._moanaWantsPlay = true;
    await load(video);
    if (video._moanaWantsPlay) safePlay(video);
  }

  function hide(video) {
    video._moanaWantsPlay = false;
    if (!video.paused) video.pause();
  }

  function start() {
    const videos = Array.from(document.querySelectorAll(SELECTOR)).filter(
      (v) => !v.dataset.moanaObserved,
    );
    if (!videos.length) return;

    if (saveData || !("IntersectionObserver" in window)) {
      // Datensparmodus: nur Posterbild, Video erst auf Antippen
      videos.forEach((v) => {
        v.dataset.moanaObserved = "1";
        v.addEventListener("click", () => show(v), { once: true });
      });
      if (saveData) return;
    }

    // "Sichtbar" = mind. 50 % des Videos ODER mind. 50 % der Bildschirmhöhe
    // (wichtig für sehr hohe Videos, die nie zu 50 % in den Bildschirm passen)
    const isShown = (entry) => {
      if (!entry.isIntersecting) return false;
      if (entry.intersectionRatio >= 0.5) return true;
      const rootH = entry.rootBounds ? entry.rootBounds.height : window.innerHeight;
      return entry.intersectionRect.height >= rootH * 0.5;
    };
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (isShown(entry)) show(entry.target);
          else hide(entry.target);
        });
      },
      { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5] },
    );
    videos.forEach((v) => {
      v.dataset.moanaObserved = "1";
      io.observe(v);
    });
  }

  function whenPageLoaded(cb) {
    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      if ("requestIdleCallback" in window) requestIdleCallback(cb, { timeout: 1500 });
      else setTimeout(cb, 200);
    };
    if (document.readyState === "complete") run();
    else window.addEventListener("load", run, { once: true });
    // Sicherheitsnetz, falls "load" durch Drittanbieter-Skripte sehr spät kommt
    setTimeout(run, 4000);
  }

  whenPageLoaded(start);
  // Theme-Editor / nachgeladene Abschnitte
  document.addEventListener("shopify:section:load", () => whenPageLoaded(start));
})();
